import React, { createRef, useContext, useEffect, useState } from "react";
import { ViewOfferContext } from "./context";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { VIEW_OFFER_TABS } from "./constants/initialValues";
import NumberCircleIcon from "../../Common/NumberCircleIcon";

const ViewOfferNav = ({
  selectedTabToView,
  setSelectedTabToView,
  setActiveTab,
  setActiveChargeTab,
}) => {
  const { values, sectionRefs, rightContentRef } = useContext(ViewOfferContext);
  const [selectedItem, setSelectedItem] = useState("offerDetails");

  const [openSections, setOpenSections] = useState({
    grants: false,
    charges: false,
    usage: false,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleNext = (tab) => {
    setSelectedTabToView(tab);
  };

  const handleScrollTo = (name) => {
    const nameRef = sectionRefs.current[name];
    if (nameRef?.current && rightContentRef?.current) {
      const container = rightContentRef.current;
      const offsetTop = nameRef.current.offsetTop - container.offsetTop;

      container.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (values?.grants?.length > 0) {
      values?.grants?.forEach((_, index) => {
        const refKey = `grants${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringGrants?.length > 0) {
      values?.recurringGrants?.forEach((_, index) => {
        const refKey = `recurringGrants${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.charges?.length > 0) {
      values?.charges?.forEach((_, index) => {
        const refKey = `charges${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringCharges?.length > 0) {
      values?.recurringCharges?.forEach((_, index) => {
        const refKey = `recurringCharges${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringUsageRatePlan?.length > 0) {
      values?.recurringUsageRatePlan?.forEach((_, index) => {
        const refKey = `recurringUsageRatePlan${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
  }, [
    values?.grants,
    values?.recurringGrants,
    values?.charges,
    values?.recurringCharges,
    values?.recurringUsageRatePlan,
  ]);

  return (
    <div className="stepper-container stepper-border ms-0 ps-2">
      {/* Step 1: Offer Details */}
      <div
        className="stepper-step"
        onClick={() => handleNext(VIEW_OFFER_TABS.OFFER_DETAILS)}
      >
        <div className={`stepper-dot`} />
        <div
          className={`fw-semibold cursor-pointer ${
            selectedTabToView === VIEW_OFFER_TABS.OFFER_DETAILS
              ? "rv-text-primary-color"
              : ""
          }`}
        >
          Offer Details
        </div>
        <hr className="me-2" />
      </div>

      {/* Step 2: Grants */}
      <div
        className="stepper-step"
        onClick={() => handleNext(VIEW_OFFER_TABS.GRANTS)}
      >
        <div
          className={`stepper-dot ${
            selectedTabToView === VIEW_OFFER_TABS.OFFER_DETAILS
              ? "inactive-dot"
              : ""
          }`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => toggleSection("grants")}
        >
          <span
            className={`${
              selectedTabToView === VIEW_OFFER_TABS.GRANTS
                ? "rv-text-primary-color"
                : ""
            }`}
          >
            Grants
          </span>
          {openSections.grants ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.grants && (
          <div className="mt-2">
            {values?.grants?.map((grant, index) => (
              <div
                key={index}
                onClick={() => {
                  setActiveTab(VIEW_OFFER_TABS.ONE_TIME);
                  setSelectedItem(`grants${index + 1}`);
                  handleScrollTo(`grants${index + 1}`);
                }}
                className="cursor-pointer pt-2"
              >
                <span
                  className={`me-1 ${
                    selectedItem === `grants${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Grant {index > 0 ? index + 1 : ""}
                </span>{" "}
                <NumberCircleIcon />
              </div>
            ))}
            {values?.recurringGrants?.map((grant, index) => (
              <div
                key={index}
                onClick={() => {
                  setActiveTab(VIEW_OFFER_TABS.RECURRING);
                  setSelectedItem(`recurringGrants${index + 1}`);
                  handleScrollTo(`recurringGrants${index + 1}`);
                }}
                className="cursor-pointer pt-2"
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringGrants${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Grant {index > 0 ? index + 1 : ""}
                </span>{" "}
                <AutorenewIcon
                  fontSize="inherit"
                  fontWeight="bold"
                  style={{ transform: "rotate(45deg)" }}
                />
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>

      {/* Step 3: Charges */}
      <div
        className="stepper-step"
        onClick={() => handleNext(VIEW_OFFER_TABS.CHARGES)}
      >
        <div
          className={`stepper-dot ${
            selectedTabToView === VIEW_OFFER_TABS.OFFER_DETAILS ||
            selectedTabToView === VIEW_OFFER_TABS.GRANTS
              ? "inactive-dot"
              : ""
          }`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => toggleSection("charges")}
        >
          <span
            className={`${
              selectedTabToView === VIEW_OFFER_TABS.CHARGES
                ? "rv-text-primary-color"
                : ""
            }`}
          >
            Charges
          </span>
          {openSections.charges ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.charges && (
          <div className="mt-1">
            {values?.charges?.map((charge, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={() => {
                  setActiveChargeTab(VIEW_OFFER_TABS.ONE_TIME);
                  setSelectedItem(`charges${index + 1}`);
                  handleScrollTo(`charges${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `charges${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Charge {index > 0 ? index + 1 : ""}
                </span>
                <NumberCircleIcon />
              </div>
            ))}
            {values?.recurringCharges?.map((charge, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={() => {
                  setActiveChargeTab(VIEW_OFFER_TABS.RECURRING);
                  setSelectedItem(`recurringCharges${index + 1}`);
                  handleScrollTo(`recurringCharges${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringCharges${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  {" "}
                  - Charge {index > 0 ? index + 1 : ""}
                </span>{" "}
                {charge?.type === "1" ? (
                  <NumberCircleIcon />
                ) : (
                  <AutorenewIcon
                    fontSize="inherit"
                    fontWeight="bold"
                    style={{ transform: "rotate(45deg)" }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>

      {/* Step 4: Usage */}
      <div
        className="stepper-step"
        onClick={() => handleNext(VIEW_OFFER_TABS.USAGE)}
      >
        <div
          className={`stepper-dot ${
            selectedTabToView !== VIEW_OFFER_TABS.USAGE ? "inactive-dot" : ""
          }`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => toggleSection("usage")}
        >
          <span
            className={`${
              selectedTabToView === VIEW_OFFER_TABS.USAGE
                ? "rv-text-primary-color"
                : ""
            }`}
          >
            Usage
          </span>
          {openSections.usage ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.usage && (
          <div className="mt-1">
            {values?.recurringUsageRatePlan?.map((usage, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={() => {
                  setSelectedItem(`recurringUsageRatePlan${index + 1}`);
                  handleScrollTo(`recurringUsageRatePlan${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringUsageRatePlan${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  {" "}
                  - Usage {index > 0 ? index + 1 : ""}
                </span>
                {usage?.type === "1" ? (
                  <NumberCircleIcon />
                ) : (
                  <AutorenewIcon
                    fontSize="inherit"
                    fontWeight="bold"
                    style={{ transform: "rotate(45deg)" }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>
    </div>
  );
};

export default ViewOfferNav;
